export const ROLE_KEYS = [
  'platform_admin',
  'participant',
  'coach',
  'coach_manager',
  'hr_admin',
  'hr_viewer',
  'department_representative',
  'account_manager',
  'benefits_triage',
] as const

export type RoleKey = (typeof ROLE_KEYS)[number]

export const PERMISSION_KEYS = [
  'platform.admin.access',
  'authorization.manage',
  'authorization.audit.view',
  'participant.portal.access',
  'coach.portal.access',
  'coach.manage',
  'hr.portal.view',
  'hr.people.manage',
  'hr.severance.view',
  'hr.severance.manage',
  'hr.documents.manage',
  'offboarding.tasks.view',
  'offboarding.tasks.update',
  'internal_mobility.view',
  'internal_mobility.manage',
  'account.manage',
  'benefits.triage',
] as const

export type PermissionKey = (typeof PERMISSION_KEYS)[number]

export const FEATURE_KEYS = [
  'external_jobs',
  'internal_jobs',
  'skills_map',
  'coaching',
  'benefits',
  'learn',
] as const

export type FeatureKey = (typeof FEATURE_KEYS)[number]

export const JOURNEY_TYPES = [
  'standard',
  'outplacement',
  'voluntary_exit',
  'internal_mobility',
  'entrepreneurship',
  'retirement',
] as const

export type JourneyType = (typeof JOURNEY_TYPES)[number]

export const PROGRAM_TYPES = [
  'everyone',
  'everyone_severance',
  'everyone_plus',
  'everyone_plus_severance',
  'internal_mobility',
  'voluntary_exit',
] as const
export type ProgramType = (typeof PROGRAM_TYPES)[number]

export const PERSONAL_GOALS = ['career', 'entrepreneurship', 'retirement'] as const
export type PersonalGoal = (typeof PERSONAL_GOALS)[number]

export const COACHING_TRACKS = ['entrepreneurship', 'retirement'] as const
export type CoachingTrack = (typeof COACHING_TRACKS)[number]

export const SEVERANCE_WORKFLOW_STATES = [
  'not_applicable',
  'election_pending',
  'elected',
  'cooling_off',
  'released',
  'cancelled',
] as const

export type SeveranceWorkflowState = (typeof SEVERANCE_WORKFLOW_STATES)[number]

export const OFFBOARDING_WORKSTREAMS = ['it', 'payroll', 'legal', 'security', 'benefits'] as const

export type OffboardingWorkstream = (typeof OFFBOARDING_WORKSTREAMS)[number]

export const SCOPE_TYPES = ['platform', 'organization'] as const
export type ScopeType = (typeof SCOPE_TYPES)[number]

export interface AuthorizationScope {
  scope_type: ScopeType
  organization_id: number | null
  roles: RoleKey[]
  permissions: PermissionKey[]
}

export interface ParticipantAccess {
  program_id: number
  organization_id: number
  cohort_id: number
  journey_type: JourneyType
  program_type: ProgramType
  coaching_track: CoachingTrack | null
  primary_goal: PersonalGoal | null
  allowed_goals: PersonalGoal[]
  organization_entitlements: FeatureKey[]
  cohort_features: FeatureKey[]
  program_features: FeatureKey[]
  effective_features: FeatureKey[]
  workflow_state: SeveranceWorkflowState
  workflow_restricted: boolean
  cooling_off_ends_at: string | null
}

export interface WorkstreamScope {
  organization_id: number
  workstream: OffboardingWorkstream
  expires_at: string | null
}

export interface AuthorizationContext {
  user_id: number
  permissions: PermissionKey[]
  scopes: AuthorizationScope[]
  participant: ParticipantAccess | null
  assigned_organization_ids: number[]
  offboarding_workstreams: WorkstreamScope[]
}

export interface RoleAssignment {
  id: number
  user_id: number
  role: RoleKey
  scope_type: ScopeType
  organization_id: number | null
  expires_at: string | null
}

export interface PortalInvitationResponse {
  user_id: number
  email: string
  roles: RoleKey[]
  portal_url: string
  sent_at: string
}

export interface RoleGrantRequest {
  role: RoleKey
  organization_id?: number | null
  expires_at?: string | null
  reason: string
}

export interface ParticipantProvisionRequest {
  journey_type: JourneyType
  program_type?: ProgramType | null
  reason: string
}

export interface RoleRevokeRequest {
  organization_id?: number | null
  reason: string
}

export interface AuthorizationAuditEvent {
  id: number
  action: string
  actor_user_id: number | null
  target_user_id: number | null
  role_key: string | null
  organization_id: number | null
  reason: string
  event_metadata: Record<string, unknown>
  created_at: string
}
